// Free postcode validation alternatives
import postcodeData from "./postcode.json";

export interface FreePostcodeResult {
  isValid: boolean;
  location?: {
    suburb: string;
    state: string;
    postcode: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  error?: string;
}

/**
 * Extract suburb name from display name in postcode data
 * Example: "2007, Ultimo, Sydney CBD, Sydney..." -> "Ultimo"
 */
const extractSuburbFromDisplayName = (displayName: string): string | null => {
  const parts = displayName.split(",").map((part) => part.trim());
  // Usually the suburb is the second part (after postcode)
  // But if the first part is not a postcode, it might be the location itself
  if (parts.length >= 2) {
    // Check if first part is a postcode (4 digits)
    if (/^\d{4}$/.test(parts[0])) {
      return parts[1] || null;
    }
    // Otherwise return the first part
    return parts[0] || null;
  }
  return parts[0] || null;
};

/**
 * Extract location name from Nominatim response with comprehensive fallback logic
 */
const extractLocationName = (result: any): string | null => {
  // Priority order for location name extraction
  const locationFields = [
    result.address?.suburb,
    result.address?.city,
    result.address?.town,
    result.address?.locality,
    result.address?.hamlet,
    result.address?.village,
    result.address?.municipality,
    result.address?.county,
    result.address?.district,
    result.name, // Fallback to the main name field
    result.display_name?.split(",")[1]?.trim(), // Extract from display name as last resort
  ];

  return locationFields.find((field) => field && typeof field === "string" && field.trim().length > 0) || null;
};

/**
 * Validate if coordinates are within Australia's approximate bounds
 */
const isWithinAustraliaBounds = (lat: number, lng: number): boolean => {
  // Australia's approximate bounding box (including territories)
  return lat >= -44 && lat <= -10 && lng >= 113 && lng <= 154;
};

/**
 * Option 1: Using OpenStreetMap Nominatim (100% Free)
 * Rate limit: 1 request per second
 * No API key required
 * First checks local postcode.json file before making API calls
 */
const validatePostcodeWithNominatim = async (postcode: string): Promise<FreePostcodeResult> => {
  if (!postcode || postcode.length !== 4 || !/^\d{4}$/.test(postcode)) {
    return {
      isValid: false,
      error: "Postcode must be exactly 4 digits",
    };
  }

  // First, search in the local postcode.json file by postcode (not by name)
  const localPostcodeData = (postcodeData as any[]).find((item) => item.postcode === postcode);

  if (localPostcodeData) {
    const suburb = extractSuburbFromDisplayName(localPostcodeData.displayName);

    if (suburb && localPostcodeData.state) {
      return {
        isValid: true,
        location: {
          suburb: suburb.trim(),
          state: localPostcodeData.state.trim(),
          postcode: postcode,
          coordinates: {
            lat: Math.round(localPostcodeData.lat * 1000000) / 1000000,
            lng: Math.round(localPostcodeData.lng * 1000000) / 1000000,
          },
        },
      };
    }
  }

  // If not found in local data, fall back to API call
  try {
    const url = new URL("https://nominatim.openstreetmap.org/search");
    url.searchParams.append("format", "json");
    url.searchParams.append("q", `${postcode}, Australia`);
    url.searchParams.append("countrycodes", "au");
    url.searchParams.append("addressdetails", "1");
    url.searchParams.append("limit", "1");

    // Be respectful - add user agent and delay
    const response = await fetch(url.toString(), {
      headers: {
        "User-Agent": "HyperKarting-PostcodeValidator/1.0",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.length > 0) {
      const result = data[0];

      // Extract location name using helper function
      const suburb = extractLocationName(result);
      // Check for both 'state' and 'territory' (ACT returns 'territory')
      const fullStateName = result.address?.state || result.address?.territory;

      // Enhanced validation with better error messages
      if (!suburb || !fullStateName) {
        // Debug logging for troubleshooting
        console.warn("Postcode validation failed for:", postcode, {
          availableAddressFields: Object.keys(result.address || {}),
          addressData: result.address,
          extractedSuburb: suburb,
          extractedState: fullStateName,
          displayName: result.display_name,
          rawResult: result,
        });

        return {
          isValid: false,
          error: !suburb ? "Unable to determine location name for this postcode" : "Unable to determine state for this postcode",
        };
      }

      // Validate coordinates
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);

      if (isNaN(lat) || isNaN(lng)) {
        console.warn("Invalid coordinates for postcode:", postcode, {
          rawLat: result.lat,
          rawLng: result.lon,
          parsedLat: lat,
          parsedLng: lng,
        });

        return {
          isValid: false,
          error: "Unable to determine coordinates for this postcode",
        };
      }

      // Validate that coordinates are within Australia bounds
      if (!isWithinAustraliaBounds(lat, lng)) {
        console.warn("Coordinates outside Australia bounds for postcode:", postcode, {
          lat,
          lng,
          suburb,
          state: fullStateName,
        });

        return {
          isValid: false,
          error: "Postcode appears to be outside Australia",
        };
      }

      return {
        isValid: true,
        location: {
          suburb: suburb.trim(),
          state: fullStateName.trim(),
          postcode: postcode,
          coordinates: {
            lat: Math.round(lat * 1000000) / 1000000, // Round to 6 decimal places
            lng: Math.round(lng * 1000000) / 1000000,
          },
        },
      };
    } else {
      return {
        isValid: false,
        error: "Postcode not found",
      };
    }
  } catch (error) {
    console.error("Nominatim error:", error);
    return {
      isValid: false,
      error: "Unable to validate postcode",
    };
  }
};

/**
 * API-based postcode validation with fallback system
 * Tries Nominatim first, then falls back to Australia Post API if not found
 */
export const validatePostcodeHybrid = async (postcode: string): Promise<FreePostcodeResult> => {
  // Try Nominatim API first (free, no API key required)
  await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay for Nominatim
  const nominatimResult = await validatePostcodeWithNominatim(postcode);

  // If Nominatim found the postcode, return the result
  if (nominatimResult.isValid) {
    return nominatimResult;
  }

  return validatePostcodeWithAustraliaPost(postcode);
};

/**
 * Australia Post API (Official but requires registration)
 * Free tier: 1000 requests per month
 */
const validatePostcodeWithAustraliaPost = async (postcode: string): Promise<FreePostcodeResult> => {
  // Note: You need to register at https://developers.auspost.com.au/
  const apiKey = process.env.REACT_APP_AUSTRALIA_POST_API_KEY;

  if (!apiKey) {
    console.log("Australia Post API key not found, cannot use as fallback");
    return {
      isValid: false,
      error: "Postcode not found",
    };
  }

  try {
    const url = new URL(`https://digitalapi.auspost.com.au/postcode/search.json`);
    url.searchParams.append("q", postcode);
    url.searchParams.append("state", ""); // Search all states

    const response = await fetch(url.toString(), {
      headers: {
        "AUTH-KEY": apiKey,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.localities && data.localities.locality && data.localities.locality.length > 0) {
      const locality = data.localities.locality[0];

      return {
        isValid: true,
        location: {
          suburb: locality.name,
          state: locality.state,
          postcode: postcode,
          coordinates: {
            lat: parseFloat(locality.latitude || "0"),
            lng: parseFloat(locality.longitude || "0"),
          },
        },
      };
    } else {
      return {
        isValid: false,
        error: "Postcode not found",
      };
    }
  } catch (error) {
    console.error("Australia Post API error:", error);
    return {
      isValid: false,
      error: "Postcode not found",
    };
  }
};
